import React, { useEffect } from "react";
import { useObserver } from "mobx-react";
import { Dialog, DialogActions, DialogTitle, DialogContent } from "@material-ui/core";

import locale from "@bonliva-crm/locale";
import { useStore } from "@hooks";
import { PrimaryButton } from "@components";

import { DocumentUploaderStore } from "./DocumentUploaderStore";
import { InitialActions } from "./InitialActions";
import { DocumentList } from "./DocumentList";
import { Fail } from "./Fail";
import { Success } from "./Success";
import { Uploading } from "./Uploading";

type Props = {
  canSubmit: boolean;
  files: File[];
  onClose: () => void;
  onUpload: () => Promise<any>;
  renderInitial?: (documentList: React.ReactNode) => React.ReactNode;
};

export function DocumentUploaderDialog(props: Props) {
  const localStore = useStore(() => new DocumentUploaderStore(props.onUpload));

  let title = "";
  if (localStore.uploadStatus === "uploading") {
    title = locale.Uploading;
  } else if (localStore.uploadStatus === "initial") {
    title = locale.UploadFile;
  }

  const renderInitial = props.renderInitial || ((x) => x);

  useEffect(() => {
    if (props.files.length === 0) {
      localStore.reset();
    }
  });

  useEffect(() => {
    localStore.uploadFiles = props.onUpload;
  }, [props.onUpload]);

  const close = () => {
    props.onClose();
    localStore.reset();
  };

  return useObserver(() => {
    const { uploadStatus } = localStore;

    return (
      <Dialog
        open={props.files.length > 0}
        TransitionProps={{
          mountOnEnter: true,
          unmountOnExit: true
        }}
        maxWidth="sm"
        fullWidth
        onBackdropClick={props.onClose}
        data-test="DocumentUploader"
        className="document-uploader"
      >
        <DialogTitle>
          {localStore.uploadStatus === "fail" ? locale.FileUploadFail : title}
        </DialogTitle>

        {uploadStatus === "fail" && (
          <Fail
            onClose={props.onClose}
            onRetry={() => null}
            error={localStore.uploadError}
          />
        )}

        {uploadStatus !== "fail" && (
          <DialogContent>
            {uploadStatus === "initial" &&
              renderInitial(<DocumentList files={props.files} />)}
            {uploadStatus === "uploading" && <Uploading />}
            {uploadStatus === "success" && <Success onClose={props.onClose} />}
          </DialogContent>
        )}

        {uploadStatus === "initial" && (
          <InitialActions
            canSubmit={props.canSubmit}
            onClose={close}
            onSubmit={() => localStore.submit()}
          />
        )}

        {uploadStatus === "success" && (
          <DialogActions>
            <PrimaryButton onClick={close}>{locale.OK}</PrimaryButton>
          </DialogActions>
        )}
      </Dialog>
    );
  });
}
